import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { hasPageAccess } from '../config/pagePermissions';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  AppBar,
  Toolbar,
  Container,
  CircularProgress,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  FormControl,
  InputLabel,
  Select,
  Chip
} from '@mui/material';
import {
  People,
  Favorite,
  Chat,
  LocationOn,
  TrendingUp,
  Report,
  ExitToApp,
  Person,
  Coffee,
  Notifications,
  Settings,
  AdminPanelSettings,
  Storage,
  Timeline,
  Psychology,
  Assessment,
  Group,
  SwipeRight,
  Star,
  Feedback as FeedbackIcon,
  NotificationImportant,
  Article,
  PhotoLibrary,
  EmojiEvents,
  Flag,
  Delete,
  Comment,
  PersonOff,
  AttachMoney,
  Store,
  QrCode2,
  Email,
  Warning,
  Quiz,
  CardGiftcard,
  LocalCafe,
  Lightbulb,
  Cloud,
  Backup,
  SystemUpdate,
  Business,
  Bolt,
  Link
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  premiumUsers: number;
  totalMatches: number;
  activeMatches: number;
  totalChats: number;
  totalReports: number;
}

interface UserGrowthData {
  date: string;
  count: number;
}

const Dashboard: React.FC = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [userGrowth, setUserGrowth] = useState<UserGrowthData[]>([]);
  const [comprehensiveStats, setComprehensiveStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [feedbackStats, setFeedbackStats] = useState<any>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [qrStats, setQrStats] = useState<any>(null);
  const [allNotifications, setAllNotifications] = useState<any[]>([]);
  const [notificationBreakdown, setNotificationBreakdown] = useState<any>(null);

  const fetchDashboardData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // Execute all API calls in parallel
      const [
        userStatsResponse,
        matchStatsResponse,
        analyticsResponse,
        growthResponse,
        comprehensiveResponse,
        feedbackStatsResponse,
        contactStatsResponse,
        qrStatsResponse,
        notificationsSummaryResponse
      ] = await Promise.all([
        axios.get('https://api.sobcof.com/api/users/stats/overview', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/matches/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/analytics/overview?timeRange=7d', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/analytics/user-growth?days=7', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`https://api.sobcof.com/api/dashboard/comprehensive-stats?timeRange=${timeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/api/feedback-stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/contact-stats', {
          headers: { 'Authorization': `Bearer ${token}` },
          params: lastCheck ? { since: lastCheck.toISOString() } : {}
        }),
        axios.get('https://api.sobcof.com/api/qr-stats/summary', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get('https://api.sobcof.com/api/admin/notifications-summary', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Parse user stats
      const userStats = userStatsResponse.data.success ? userStatsResponse.data.data : {};
      const matchStats = matchStatsResponse.data.success ? matchStatsResponse.data.data : {};
      const analyticsStats = analyticsResponse.data.success ? analyticsResponse.data.data : {};

      console.log('Stats received:', { userStats, matchStats, analyticsStats });

      setStats({
        totalUsers: userStats.totalUsers || 0,
        activeUsers: userStats.activeUsers || 0,
        premiumUsers: userStats.premiumUsers || 0,
        totalMatches: matchStats.totalMatches || 0,
        activeMatches: matchStats.activeMatches || 0,
        totalChats: analyticsStats.totalChats || 0,
        totalReports: analyticsStats.totalReports || 0
      });

      if (growthResponse.data.success) {
        setUserGrowth(growthResponse.data.data);
      }

      if (comprehensiveResponse.data.success) {
        setComprehensiveStats(comprehensiveResponse.data.data);
      }

      // Set feedback stats
      if (feedbackStatsResponse.data.success) {
        setFeedbackStats(feedbackStatsResponse.data.data);
      }

      // Set contact stats
      if (contactStatsResponse.data.success) {
        const data = contactStatsResponse.data.data;
        if (!lastCheck || data.hasChanges !== false) {
          const newMessages = data.new || 0;
          setNotificationCount(newMessages);
          setLastCheck(new Date());
          console.log(`📊 Contact stats updated: ${newMessages} new messages`);
        } else {
          console.log('📊 No changes detected, skipping update');
        }
      }

      // Set QR stats
      if (qrStatsResponse.data.success) {
        setQrStats(qrStatsResponse.data.data);
      }

      // Set notifications summary
      if (notificationsSummaryResponse.data.success) {
        const notifData = notificationsSummaryResponse.data.data;
        setNotificationCount(notifData.total || 0);
        setNotificationBreakdown(notifData.breakdown);
        setAllNotifications(notifData.recent || []);
        console.log(`📊 Notifications updated: ${notifData.total} total notifications`);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, lastCheck]);

  useEffect(() => {
    fetchDashboardData();

    // Her 2 dakikada bir bildirimleri kontrol et (optimizasyon)
    const interval = setInterval(() => {
      // Sadece sayfa görünürken kontrol et
      if (!document.hidden) {
        fetchDashboardData();
      }
    }, 120000); // 2 dakika

    // Sayfa görünür hale geldiğinde kontrol et
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchDashboardData]);

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    // Bildirimler zaten yüklü, tekrar çekmeye gerek yok
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Icon seçimi için helper fonksiyon
  const getNotificationIcon = (iconName: string) => {
    switch (iconName) {
      case 'email':
        return <Email sx={{ fontSize: 20 }} />;
      case 'feedback':
        return <FeedbackIcon sx={{ fontSize: 20 }} />;
      case 'store':
        return <Store sx={{ fontSize: 20 }} />;
      case 'warning':
        return <Warning sx={{ fontSize: 20 }} />;
      default:
        return <NotificationImportant sx={{ fontSize: 20 }} />;
    }
  };

  // Bildirim rengi için helper fonksiyon
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'contact':
        return '#2196F3';
      case 'feedback':
        return '#4CAF50';
      case 'cafe-application':
        return '#9c88ff';
      case 'system':
        return '#ff4444';
      default:
        return '#8B4513';
    }
  };

  // Consistent color theme - Coffee/Brown tones
  const COLORS = ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F4A460'];
  const PRIMARY_COLOR = '#8B4513'; // Saddle Brown
  const SECONDARY_COLOR = '#D2691E'; // Chocolate
  const ACCENT_COLOR = '#CD853F'; // Peru

  // Sayfa erişim kontrolü - admin izinlerine göre
  const canAccess = useMemo(() => {
    return (pageKey: string): boolean => {
      if (!admin) return false;
      return hasPageAccess(admin.permissions || [], pageKey, admin.role);
    };
  }, [admin]);

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={80} sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6" color="white">
            Yükleniyor...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* Enhanced App Bar */}
      <AppBar
        position="static"
        sx={{
          background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Avatar sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              mr: 2,
              width: 45,
              height: 45
            }}>
              <Coffee sx={{ fontSize: 24 }} />
            </Avatar>
            <Typography variant="h5" component="div" sx={{
              fontWeight: 700,
              color: 'white'
            }}>
              SobCof Admin
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              size="large"
              onClick={handleNotificationClick}
              sx={{ position: 'relative' }}
            >
              <Badge badgeContent={notificationCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* Bildirim Dropdown Menüsü */}
            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={handleNotificationClose}
              PaperProps={{
                sx: {
                  width: 350,
                  maxHeight: 400,
                  mt: 1
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#8B4513' }}>
                  Bildirimler
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {notificationCount} yeni bildirim
                </Typography>
                {notificationBreakdown && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {notificationBreakdown.contacts > 0 && (
                      <Chip
                        label={`${notificationBreakdown.contacts} Mesaj`}
                        size="small"
                        sx={{ bgcolor: '#2196F3', color: 'white', fontSize: '11px' }}
                      />
                    )}
                    {notificationBreakdown.feedbacks > 0 && (
                      <Chip
                        label={`${notificationBreakdown.feedbacks} Geri Bildirim`}
                        size="small"
                        sx={{ bgcolor: '#4CAF50', color: 'white', fontSize: '11px' }}
                      />
                    )}
                    {notificationBreakdown.cafeApplications > 0 && (
                      <Chip
                        label={`${notificationBreakdown.cafeApplications} Başvuru`}
                        size="small"
                        sx={{ bgcolor: '#9c88ff', color: 'white', fontSize: '11px' }}
                      />
                    )}
                    {notificationBreakdown.systemAlerts > 0 && (
                      <Chip
                        label={`${notificationBreakdown.systemAlerts} Şikayet`}
                        size="small"
                        sx={{ bgcolor: '#ff4444', color: 'white', fontSize: '11px' }}
                      />
                    )}
                  </Box>
                )}
              </Box>

              {allNotifications.length > 0 ? (
                allNotifications.map((notification, index) => (
                  <MenuItem
                    key={notification.id || index}
                    onClick={() => {
                      handleNotificationClose();
                      navigate(notification.route);
                    }}
                    sx={{
                      py: 1.5,
                      borderBottom: index < allNotifications.length - 1 ? '1px solid #f0f0f0' : 'none',
                      '&:hover': {
                        bgcolor: 'rgba(139, 69, 19, 0.05)'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{
                        width: 36,
                        height: 36,
                        bgcolor: getNotificationColor(notification.type),
                      }}>
                        {getNotificationIcon(notification.icon)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14px' }}>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '13px' }}>
                            {notification.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                            {notification.createdAt ? new Date(notification.createdAt).toLocaleString('tr-TR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <ListItemText
                    primary="Henüz bildirim yok"
                    secondary="Yeni mesajlar, başvurular ve sistem bildirimleri burada görünecek"
                  />
                </MenuItem>
              )}
            </Menu>
            <IconButton
              color="inherit"
              size="large"
              onClick={() => {
                // Ayarlar sayfasına git
                navigate('/admin/admin-management');
              }}
            >
              <Settings />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 35, height: 35, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <Person />
              </Avatar>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {admin?.username}
              </Typography>
            </Box>
            <IconButton color="inherit" onClick={handleLogout} size="large">
              <ExitToApp />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Enhanced Quick Stats Cards - Only for analytics permission */}
        {canAccess('analytics') && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255,255,255,0.1)',
                  transform: 'skewY(-6deg)',
                  transformOrigin: 'top left'
                }
              }}>
                <People sx={{ fontSize: 50, mb: 2, position: 'relative', zIndex: 1 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, position: 'relative', zIndex: 1 }}>
                  {stats?.totalUsers || 0}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9, position: 'relative', zIndex: 1 }}>
                  Toplam Kullanıcı
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, ${ACCENT_COLOR} 100%)`,
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Favorite sx={{ fontSize: 50, mb: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats?.totalMatches || 0}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Toplam Eşleşme
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, #DEB887 100%)`,
                color: 'white',
                borderRadius: 3
              }}>
                <Chat sx={{ fontSize: 50, mb: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats?.totalChats || 0}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Aktif Sohbet
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                textAlign: 'center',
                background: `linear-gradient(135deg, #DEB887 0%, #F4A460 100%)`,
                color: 'white',
                borderRadius: 3
              }}>
                <TrendingUp sx={{ fontSize: 50, mb: 2 }} />
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {stats?.premiumUsers || 0}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Premium Kullanıcı
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Enhanced Navigation Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          {canAccess('users') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/users')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Person sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kullanıcı Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcıları görüntüle, düzenle ve yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('users') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, ${ACCENT_COLOR} 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/stories')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <PhotoLibrary sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Hikayeler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcı hikayelerini yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('users') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #F1C40F 0%, #F5B041 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(241, 196, 15, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/game-stats')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <EmojiEvents sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Zirve Yarışı
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Oyun istatistikleri ve liderlik tablosu
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('matches') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, ${ACCENT_COLOR} 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(240, 147, 251, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/matches')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Favorite sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Eşleşmeler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcı eşleşmelerini takip et
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('map') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, #DEB887 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(79, 172, 254, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/map')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <LocationOn sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Konum Haritası
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcı konumlarını haritada görüntüle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('chats') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #DEB887 0%, #F4A460 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(67, 233, 123, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/chats')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Chat sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Sohbetler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcı sohbetlerini izle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('reports') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #F4A460 0%, #DEB887 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(250, 112, 154, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/reports')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Report sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Şikayetler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcı şikayetlerini yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('game-management') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #CD853F 0%, #DEB887 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(205, 133, 63, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/game-management')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Quiz sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Oyun Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kahve oyunu sorularını yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Günlük Dashboard */}
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                color: 'white',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 3
              }}
              onClick={() => navigate('/admin/daily-dashboard')}
            >
              <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                <Avatar sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  mx: 'auto',
                  mb: 3
                }}>
                  <Timeline sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  ☀️ Sabah Raporu
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Bugünün kritik metrikleri
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Kişisel AI Asistanım */}
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card
              sx={{
                cursor: 'pointer',
                height: '100%',
                background: `linear-gradient(135deg, #111827 0%, #312e81 100%)`,
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 20px 40px rgba(79, 70, 229, 0.4)'
                },
                transition: 'all 0.3s ease',
                borderRadius: 3
              }}
              onClick={() => navigate('/admin/my-assistant')}
            >
              <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                <Avatar sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  mx: 'auto',
                  mb: 3,
                  border: '2px solid rgba(245, 158, 11, 0.5)'
                }}>
                  <Psychology sx={{ fontSize: 40, color: '#f59e0b' }} />
                </Avatar>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  🧠 Kişisel AI Asistanım
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Sesli/Yazılı Hatırlatıcı ve Gün Planlama
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* VS Kapışma Soruları */}
          {canAccess('vs-questions') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(231, 76, 60, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/vs-questions')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Bolt sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    VS Kapışma Soruları
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    1v1 kapışma sorularını yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('winners-management') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(255, 215, 0, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/winners-management')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <EmojiEvents sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kazananlar
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Oyun kazananlarını yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {canAccess('analytics') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, rgba(139, 69, 19, 0.1) 0%, rgba(210, 105, 30, 0.1) 100%)`,
                  color: '#8B4513',
                  border: '1px solid rgba(139, 69, 19, 0.2)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 20px 40px rgba(168, 237, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/analytics')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(45, 55, 72, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <TrendingUp sx={{ fontSize: 40, color: '#8B4513' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Analitik
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Detaylı istatistikleri görüntüle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Marketing & Growth Dashboard Card - NEW! */}
          {canAccess('marketing') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                  background: 'linear-gradient(135deg, #1877f2 0%, #8B4513 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(24, 119, 242, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/marketing')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Timeline sx={{ fontSize: 40, color: 'white' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
                    Marketing & Growth
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                    Meta Ads, büyüme ve dönüşüm analizi
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* API Health Monitor Card */}
          {canAccess('api-health') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(26, 26, 46, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/api-health')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(0, 255, 136, 0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Cloud sx={{ fontSize: 40, color: '#00ff88' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
                    API Health Monitor
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                    Backend & Firebase durumu
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Cafe CRM Card */}
          {canAccess('cafe-crm') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  background: `linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(99, 102, 241, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/cafe-crm')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Store sx={{ fontSize: 40, color: 'white' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
                    Kafe CRM
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                    Kafe iletişim takibi
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Admin Management Card - Only for super_admin */}
          {admin?.role === 'super_admin' && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/admin-management')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <AdminPanelSettings sx={{ fontSize: 40, color: '#D2691E' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Admin Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Admin kullanıcılarını yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Push Notifications Card */}
          {canAccess('notifications') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  background: `linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(255, 107, 107, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/notifications')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Notifications sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Push Bildirimler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kullanıcılara bildirim gönder
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Firebase Data Card */}
          {canAccess('firebase-data') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(255, 152, 0, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/firebase-data')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Storage sx={{ fontSize: 40, color: '#CD853F' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Firebase Verileri
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Tüm koleksiyonları görüntüle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Database Backup Card */}
          {canAccess('backup') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/backup')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(139, 69, 19, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Backup sx={{ fontSize: 40, color: '#8B4513' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Veritabanı Yedekleme
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Yedek al, listele ve geri yükle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* App Version Enforcement Card */}
          {canAccess('version') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/version')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(139, 69, 19, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <SystemUpdate sx={{ fontSize: 40, color: '#8B4513' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Versiyon Kontrolü
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    iOS & Android sürüm zorunluluğu ayarla
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Deleted Accounts Card */}
          {canAccess('deleted-accounts') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(229, 57, 53, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/deleted-accounts')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(229, 57, 53, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <PersonOff sx={{ fontSize: 40, color: '#e53935' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Silinmiş Hesaplar
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Silinen kullanıcıları yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Feedback Management */}
          {canAccess('feedback') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(76, 175, 80, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/feedback')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <FeedbackIcon sx={{ fontSize: 40, color: '#4CAF50' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Geri Bildirimler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Kullanıcı geri bildirimleri
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label={`${feedbackStats?.new || 0} Yeni`}
                      color="error"
                      size="small"
                      icon={<NotificationImportant />}
                    />
                    <Chip
                      label={`${feedbackStats?.averageRating || 0} ⭐`}
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Contact Messages Card */}
          {canAccess('contacts') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  border: '1px solid #e2e8f0',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(33, 150, 243, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/contacts')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Notifications sx={{ fontSize: 40, color: '#2196F3' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    İletişim Mesajları
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Kullanıcı iletişim mesajları
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Yeni Mesajlar"
                      color="primary"
                      size="small"
                      icon={<NotificationImportant />}
                    />
                    <Chip
                      label="E-posta Bildirimi"
                      color="success"
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Posts Management Card */}
          {canAccess('posts') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(255, 107, 107, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/posts')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Article sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Post Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Postları ve raporları yönet
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Post Silme"
                      color="error"
                      size="small"
                      icon={<Delete />}
                    />
                    <Chip
                      label="Rapor İnceleme"
                      color="warning"
                      size="small"
                      icon={<Flag />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Comments Management Card */}
          {canAccess('comments') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/comments')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Comment sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Yorum Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Yorumları listele ve yönet
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Yorum Silme"
                      color="error"
                      size="small"
                      icon={<Delete />}
                    />
                    <Chip
                      label="Gizleme"
                      color="warning"
                      size="small"
                      icon={<Flag />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Linktree Management Card */}
          {canAccess('linktree') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, #8B4513 0%, #CD853F 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/linktree')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Link sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Linktree Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Sosyal medya Linktree sayfanızı yönetin
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Bağlantılar"
                      color="primary"
                      size="small"
                    />
                    <Chip
                      label="Sosyal Medya"
                      color="secondary"
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Partner Cafes Card */}
          {canAccess('partner-cafes') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/partner-cafes')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Coffee sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Partner Kafeler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kafe noktalarını yönet
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Harita Entegrasyonu"
                      color="success"
                      size="small"
                      icon={<LocationOn />}
                    />
                    <Chip
                      label="Fiyat Seviyeleri"
                      color="warning"
                      size="small"
                      icon={<AttachMoney />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Franchise & Zincir Yönetimi Card */}
          {canAccess('partner-cafes') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  background: 'linear-gradient(135deg, #A0522D 0%, #D2691E 100%)',
                  color: 'white',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(160, 82, 45, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/franchises')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Business sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Franchise & Zincirler
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Zincir marka ve şubeleri yönet
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Zincir Markalar"
                      color="info"
                      size="small"
                    />
                    <Chip
                      label="Şube Yetkileri"
                      color="secondary"
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* İş Ortakları Yönetimi */}
          {canAccess('partners') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(46, 204, 113, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/partners')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Coffee sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    İş Ortakları
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Partner logolarını yönet
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Kafe Başvuruları */}
          {canAccess('cafe-applications') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #6a4c93 0%, #9c88ff 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(106, 76, 147, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/cafe-applications')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Store sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kafe Başvuruları
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Partnerlik başvurularını yönet
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Onay Süreci"
                      color="warning"
                      size="small"
                      icon={<Assessment />}
                    />
                    <Chip
                      label="Mail Sistemi"
                      color="info"
                      size="small"
                      icon={<Notifications />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* QR Code Statistics */}
          {canAccess('qr-statistics') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0, 201, 255, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/qr-statistics')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <QrCode2 sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    QR Kod İstatistikleri
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kafe QR kod taramaları
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label={`${qrStats?.totalScans || 0} Toplam Tarama`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                    <Chip
                      label={`${qrStats?.todayScans || 0} Bugün`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Retention Dashboard Card */}
          {canAccess('retention-dashboard') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/retention-dashboard')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Timeline sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kullanıcı Elde Tutma
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    DAU/MAU, Churn ve Retention analizi
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Retention"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<TrendingUp />}
                    />
                    <Chip
                      label="Churn"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<Assessment />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Cafe ROI Dashboard Card */}
          {canAccess('cafe-roi-dashboard') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(240, 147, 251, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/cafe-roi-dashboard')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Coffee sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kafe ROI Analizi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Kafe performansı ve değerlendirmeleri
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Değerlendirmeler"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<Star />}
                    />
                    <Chip
                      label="ROI"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<AttachMoney />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Content Moderation Card */}
          {canAccess('content-moderation') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(250, 112, 154, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/content-moderation')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Flag sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    İçerik Moderasyonu
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Post ve yorum raporları yönetimi
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Raporlar"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<Flag />}
                    />
                    <Chip
                      label="Moderasyon"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        fontWeight: 600
                      }}
                      size="small"
                      icon={<Delete />}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Notification Analytics Card */}
          {canAccess('notification-analytics') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#2d3748',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(168, 237, 234, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/notification-analytics')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(45, 55, 72, 0.1)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Notifications sx={{ fontSize: 40, color: '#2d3748' }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Bildirim Analizi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Push bildirim performansı
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Chip
                      label="Açılma Oranı"
                      sx={{
                        bgcolor: 'rgba(45, 55, 72, 0.1)',
                        color: '#2d3748',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                    <Chip
                      label="İletim"
                      sx={{
                        bgcolor: 'rgba(45, 55, 72, 0.15)',
                        color: '#2d3748',
                        fontWeight: 600
                      }}
                      size="small"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Gift Cards Management Card */}
          {canAccess('gift-cards') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(255, 107, 107, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/gift-cards')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <CardGiftcard sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Gift Card Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Gift card tanımları ve kodlar
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Bean Management Card */}
          {canAccess('beans') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/beans')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <LocalCafe sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Coffee Bean Yönetimi
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Harita bean spawn yönetimi
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Letter Hint Pricing Card */}
          {canAccess('letter-hint-pricing') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #ffd89b 0%, #19547b 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(255, 216, 155, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/letter-hint-pricing')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <Lightbulb sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Harf İpucu Fiyatlandırma
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Oyun ipucu fiyatlarını ayarla
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Cafe Map Card */}
          {canAccess('cafe-map') && (
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Card
                sx={{
                  background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(139, 69, 19, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                  borderRadius: 3
                }}
                onClick={() => navigate('/admin/cafe-map')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                  <Avatar sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 3
                  }}>
                    <LocationOn sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Kafeler Haritası
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Tüm kafeleri haritada görüntüle
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>

        {/* Comprehensive Analytics - Only for analytics permission */}
        {canAccess('analytics') && comprehensiveStats && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment color="primary" />
              Kapsamlı Analitik
            </Typography>

            {/* Time Range Selector */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Zaman Aralığı</InputLabel>
                <Select
                  value={timeRange}
                  label="Zaman Aralığı"
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <MenuItem value="7d">Son 7 Gün</MenuItem>
                  <MenuItem value="30d">Son 30 Gün</MenuItem>
                  <MenuItem value="90d">Son 90 Gün</MenuItem>
                  <MenuItem value="1y">Son 1 Yıl</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Engagement Metrics */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Card elevation={0} sx={{
                flex: '1 1 200px',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(139, 69, 19, 0.1)' }}>
                    <SwipeRight sx={{ color: '#8B4513' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {comprehensiveStats.engagement?.matchRate?.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Eşleşme Oranı
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card elevation={0} sx={{
                flex: '1 1 200px',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(210, 105, 30, 0.1)' }}>
                    <Star sx={{ color: '#D2691E' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {comprehensiveStats.engagement?.averageRating?.toFixed(1) || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ortalama Puan
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card elevation={0} sx={{
                flex: '1 1 200px',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(205, 133, 63, 0.1)' }}>
                    <Group sx={{ color: '#CD853F' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {comprehensiveStats.social?.acceptanceRate?.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Arkadaşlık Kabul Oranı
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card elevation={0} sx={{
                flex: '1 1 200px',
                border: '1px solid #e2e8f0',
                borderRadius: 3,
                p: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(244, 164, 96, 0.1)' }}>
                    <Timeline sx={{ color: '#F4A460' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {comprehensiveStats.userDemographics?.activeRate?.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aktif Kullanıcı Oranı
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>
          </Box>
        )}

        {/* Enhanced Charts - Only for analytics permission */}
        {canAccess('analytics') && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
            {/* User Growth Chart */}
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Kullanıcı Büyümesi (Son 7 Gün)
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#667eea"
                      strokeWidth={3}
                      dot={{ fill: '#667eea', strokeWidth: 2, r: 6 }}
                      activeDot={{ r: 8, stroke: '#667eea', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>

            {/* User Distribution Chart */}
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Paper elevation={0} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #e2e8f0'
              }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Kullanıcı Dağılımı
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Aktif', value: stats?.activeUsers || 0 },
                        { name: 'Premium', value: stats?.premiumUsers || 0 },
                        { name: 'Pasif', value: (stats?.totalUsers || 0) - (stats?.activeUsers || 0) }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent ? (percent * 100).toFixed(0) : 0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {COLORS.map((color, index) => (
                        <Cell key={`cell-${index}`} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
